import { useEffect, useState, useRef } from 'react';

const Config = require('../../../../config/default.env').default;

// Global tracker to prevent duplicate AdSense initialization
const initializedElements = new Set();

/**
 * Custom hook to handle AdSense fallback logic and initialization
 * @param {string} adId - The unique ID of the ad element
 * @returns {boolean} - Returns true if ad should show fallback placeholder
 */
export const useAdSenseFallback = (adId) => {
  const [shouldShowFallback, setShouldShowFallback] = useState(true);
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    // Always show fallback in test mode or if AdSense is disabled
    if (Config.GOOGLE_ADSENSE_TEST_MODE || !Config.GOOGLE_ADSENSE_ENABLED) {
      setShouldShowFallback(true);
      return;
    }

    // Use a short delay to ensure the component has rendered and the ad element is in the DOM
    const timer = setTimeout(() => {
      const adElement = document.getElementById(adId);
      if (!adElement) {
        return;
      }

      // Set up the observer to watch for when Google updates the ad status
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (
            mutation.type === 'attributes' &&
            mutation.attributeName === 'data-ad-status'
          ) {
            const adStatus = adElement.getAttribute('data-ad-status');
            setShouldShowFallback(adStatus !== 'filled');
          }
        });
      });

      // Start oberving the ad element immediately
      observer.observe(adElement, {
        attributes: true,
        attributeFilter: ['data-ad-status'],
      });

      // Now, check the initial state. If the ad is already filled (e.g., on a fast connection or page refresh),
      // update the state immediately.
      const existingStatus = adElement.getAttribute('data-ad-status');
      if (existingStatus === 'filled') {
        setShouldShowFallback(false);
      } else {
        // If the ad hasn't been initialized yet, push it to AdSense.
        // The observer is already in place to catch the result.
        if (
          !hasInitializedRef.current &&
          !initializedElements.has(adId) &&
          window.adsbygoogle
        ) {
          try {
            (window.adsbygoogle = window.adsbygoogle || []).push({});
            hasInitializedRef.current = true;
            initializedElements.add(adId);
          } catch (e) {
            setShouldShowFallback(true);
            initializedElements.add(adId);
          }
        }
      }

      return () => {
        observer.disconnect();
      };
    }, 50);

    return () => {
      clearTimeout(timer);
    };
  }, [adId]);

  return shouldShowFallback;
};
